import { parseAiSourceFrame } from "./aiSourceFrame";
import { polygonRings, polygonRingArea } from "./measurementGeometry";
import type { Doc } from "../_generated/dataModel";

interface Point {
  x: number;
  y: number;
}
type TakeoffMeasurement = Doc<"takeoffPilotMeasurements">;
export type MeasurementForCalculation = Pick<
  TakeoffMeasurement,
  "tool" | "geometry" | "measurementType" | "height"
>;

export function resolvePageAspectRatio(
  pageAspectRatio: number | undefined,
): number {
  return Number.isFinite(pageAspectRatio) && (pageAspectRatio as number) > 0
    ? (pageAspectRatio as number)
    : 1;
}

export function calculatePageDistance(
  first: Point,
  second: Point,
  pageAspectRatio: number = 1,
): number {
  return Math.hypot(
    second.x - first.x,
    (second.y - first.y) * resolvePageAspectRatio(pageAspectRatio),
  );
}

function calculateLineLength(
  vertices: Point[],
  pageAspectRatio: number = 1,
): number {
  if (vertices.length < 2) return 0;
  let totalLength = 0;
  for (let index = 0; index < vertices.length - 1; index += 1) {
    totalLength += calculatePageDistance(
      vertices[index]!,
      vertices[index + 1]!,
      pageAspectRatio,
    );
  }
  return totalLength;
}

function calculatePolygonPerimeter(
  vertices: Point[],
  pageAspectRatio: number = 1,
): number {
  if (vertices.length < 2) return 0;
  if (vertices.length === 2)
    return calculateLineLength(vertices, pageAspectRatio);
  return (
    calculateLineLength(vertices, pageAspectRatio) +
    calculatePageDistance(
      vertices[vertices.length - 1]!,
      vertices[0]!,
      pageAspectRatio,
    )
  );
}

function calculatePolygonArea(
  vertices: Point[],
  pageAspectRatio: number = 1,
): number {
  return polygonRingArea(vertices) * resolvePageAspectRatio(pageAspectRatio);
}

function pixelsToFeet(pixels: number, pixelsPerFoot: number): number {
  return pixels / pixelsPerFoot;
}

function calculatePolygonAreaFeet(
  vertices: Point[],
  pixelsPerFoot: number,
  pageAspectRatio: number = 1,
): number {
  const areaInPixels = calculatePolygonArea(vertices, pageAspectRatio);
  const feetPerPixel = 1 / pixelsPerFoot;
  return areaInPixels * feetPerPixel * feetPerPixel;
}

function computeLfValue(
  measurement: MeasurementForCalculation,
  pixelsPerFoot: number,
  pageAspectRatio: number = 1,
): number {
  const { tool, geometry } = measurement;

  if (tool === "line" || tool === "polyline") {
    const vertices = geometry.type === "poly" ? geometry.vertices : [];
    return pixelsToFeet(
      calculateLineLength(vertices, pageAspectRatio),
      pixelsPerFoot,
    );
  }

  if (tool === "rectangle") {
    if (geometry.type !== "rect") return 0;
    const widthFeet = pixelsToFeet(geometry.width, pixelsPerFoot);
    const heightFeet = pixelsToFeet(
      geometry.height * resolvePageAspectRatio(pageAspectRatio),
      pixelsPerFoot,
    );
    return 2 * (widthFeet + heightFeet);
  }

  return geometry.type === "poly"
    ? pixelsToFeet(
        polygonRings(geometry).reduce((sum, ring) => sum + calculatePolygonPerimeter(ring, pageAspectRatio), 0),
        pixelsPerFoot,
      )
    : 0;
}

function computeSqftValue(
  measurement: MeasurementForCalculation,
  pixelsPerFoot: number,
  pageAspectRatio: number = 1,
): number {
  const { tool, geometry, height } = measurement;

  if (tool === "line" || tool === "polyline") {
    const vertices = geometry.type === "poly" ? geometry.vertices : [];
    const lengthFeet = pixelsToFeet(
      calculateLineLength(vertices, pageAspectRatio),
      pixelsPerFoot,
    );
    return lengthFeet * (height ?? 1);
  }

  if (tool === "rectangle") {
    if (geometry.type !== "rect") return 0;
    const widthFeet = pixelsToFeet(geometry.width, pixelsPerFoot);
    const heightFeet = pixelsToFeet(
      geometry.height * resolvePageAspectRatio(pageAspectRatio),
      pixelsPerFoot,
    );
    return widthFeet * heightFeet;
  }

  return geometry.type === "poly"
    ? calculatePolygonAreaFeet(geometry.vertices, pixelsPerFoot, pageAspectRatio) -
      (geometry.holes ?? []).reduce((sum, ring) => sum + calculatePolygonAreaFeet(ring, pixelsPerFoot, pageAspectRatio), 0)
    : 0;
}

function computeCfValue(
  measurement: MeasurementForCalculation,
  pixelsPerFoot: number,
  pageAspectRatio: number = 1,
): number {
  return (
    computeSqftValue(measurement, pixelsPerFoot, pageAspectRatio) *
    (measurement.height ?? 1)
  );
}

export function recomputeTakeoffMeasurementValue(
  measurement: MeasurementForCalculation,
  scale: Pick<Doc<"measurementScales">, "pixelsPerFoot" | "pageAspectRatio">,
): number {
  return recomputeMeasurementValue(
    measurement,
    scale.pixelsPerFoot,
    resolvePageAspectRatio(scale.pageAspectRatio),
  );
}

export function isCompatiblePageScale(
  scale: Pick<
    Doc<"measurementScales">,
    "pixelsPerFoot" | "pageAspectRatio"
  > | null,
  sourceFrameJson?: string,
): boolean {
  const size = parseAiSourceFrame(sourceFrameJson).pageSizePoints;
  const valid =
    scale &&
    Number.isFinite(scale.pixelsPerFoot) &&
    scale.pixelsPerFoot > 0 &&
    size &&
    Math.abs((scale.pageAspectRatio ?? 1) - size[1] / size[0]) < 1e-6;
  return Boolean(valid);
}

/** AI quantities use the same geometry calculator, with an explicit source-frame calibration guard. */
export function computeAiMeasurementQuantity(
  measurement: MeasurementForCalculation,
  scale: Pick<
    Doc<"measurementScales">,
    "pixelsPerFoot" | "pageAspectRatio"
  > | null,
  sourceFrameJson?: string,
): { value: number; aiQuantityPending: boolean } {
  if (measurement.measurementType === "count")
    return { value: 1, aiQuantityPending: false };
  const valid = isCompatiblePageScale(scale, sourceFrameJson);
  const value = valid
    ? recomputeTakeoffMeasurementValue(measurement, scale!)
    : 0;
  return {
    value: Number.isFinite(value) && value > 0 ? value : 0,
    aiQuantityPending: !valid || !Number.isFinite(value) || value <= 0,
  };
}

export function recomputeMeasurementValue(
  measurement: MeasurementForCalculation,
  pixelsPerFoot: number,
  pageAspectRatio: number = 1,
): number {
  if (measurement.measurementType === "count") return 1;
  if (measurement.measurementType === "LF") {
    return computeLfValue(measurement, pixelsPerFoot, pageAspectRatio);
  }

  if (measurement.measurementType === "SQFT") {
    return computeSqftValue(measurement, pixelsPerFoot, pageAspectRatio);
  }

  if (measurement.measurementType === "CF") {
    return computeCfValue(measurement, pixelsPerFoot, pageAspectRatio);
  }

  return 0;
}
